import * as OT from '@opentok/client';
import { useEffect, useState } from 'react';
import { getCurrentVirtualBG } from '../../utils/video/opentok-connection';
import * as Alerts from '../../components/general-components/alert-service';
import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../stores/types/types';

interface ExportProps {
    publisher: OT.Publisher | undefined,
    videoEl: HTMLVideoElement | undefined,
}

const buildPublisherOptions = () => {
    const baseOps: OT.PublisherProperties = {
        width: '100%', 
        height: '100%',
        mirror: false,
        resolution: '1920x1080',
        insertDefaultUI: false
    }
    const videoSource = localStorage.getItem('camera');
    if (videoSource) {
        baseOps.publishVideo = true;
        baseOps.videoSource = videoSource;
    }
    const audioSource = localStorage.getItem('microphone');
    if (audioSource) {
        baseOps.publishAudio = true;
        baseOps.audioSource = audioSource;
    }
    const videoBackground = getCurrentVirtualBG();
    if (videoBackground) {
        baseOps.videoFilter = videoBackground;
    }
    return baseOps;
}

export default function useTempPublisher(initPublisher: boolean): ExportProps {
    const [publisher, setPublisher] = useState<OT.Publisher>();
    const [videoEl, setVideoEl] = useState<HTMLVideoElement>();

    const selectedAudio = useSelector((s: GS) => s.VideoReducer.selectedAudio);
    const selectedVideo = useSelector((s: GS) => s.VideoReducer.selectedVideo);
    
    const showDeviceError = (type: string) => {
        Alerts.showAlert({
            message: "Error",
            description: `Unable to publish ${type}.`,
            duration: 5000,
            type: "error"
        })
    }

    useEffect(() => {
        if (selectedAudio?.deviceId && publisher) {
            publisher.setAudioSource(selectedAudio.deviceId).catch((err) => {
                if (err) showDeviceError('audio');
            });
        }
    }, [selectedAudio]);

    useEffect(() => {
        if (selectedVideo?.deviceId && publisher) {
            publisher.setVideoSource(selectedVideo.deviceId).catch((err) => {
                if (err) showDeviceError('video');
            });
        }
    }, [selectedVideo]);
    

    function createPublisher() {
        const nPublisher = OT.initPublisher(undefined, buildPublisherOptions());

        setPublisher(nPublisher);

        nPublisher.on('videoElementCreated', (event) => {
            if (event.element instanceof HTMLVideoElement) {
                setVideoEl(event.element);
            }
        });

        return nPublisher;
    }

    useEffect(() => {
        if (publisher || !initPublisher) return;

        try {
            createPublisher();
        } catch(err) {
            showDeviceError('video');
        }
    }, [initPublisher]);

    return {
        videoEl,
        publisher
    }
}